import PropTypes from 'prop-types';
import { prop, isEmpty } from 'ramda';
import { Component } from 'react';
import { connect } from 'react-redux';
import * as LocusLabsMap from '@wayfinder/vms-map';
import * as LliamUtilities from '@wayfinder/lliam-utilities';
import * as VmsState from '../../backend/index-vms-state.js';
import { Menu, ModalWrapper } from '../../components/index.js';
import Venue from '../venue/Venue.jsx';
import Venues from '../venues/Venues.jsx';
import VisitorInsights from '../visitor-insights/VisitorInsights.jsx';
import SpatialInsights from '../spatial-insights/SpatialInsights.jsx';
import CommandLineToolsView from '../command-line-tools-view.jsx';
import ErrorPage from '../error-page/ErrorPage.jsx';
import ImportExportView from '../venue/ImportExportContainer.jsx';

import s from './Dashboard.module.scss';
import MODWizard from '../mod-wizard/MODWizard.jsx';

const { Routing } = VmsState;

const COMMAND_LINE_TOOLS = 'Command Line Tools';
const IMPORT_EXPORT = 'Import/Export';
const MOD_WIZARD = 'MapsOnDisplay Wizard';
const VISITOR_INSIGHTS = 'Visitor Insights';
const SPATIAL_INSIGHTS = 'Spatial Insights';

const menuActionCreators = {
    venues: Routing.Actions.goToVenues,
    [MOD_WIZARD]: Routing.Actions.goToModWizard,
    help: Routing.Actions.goToPlayground,
    poi: Routing.Actions.goToDashboard,
    accounts: Routing.Actions.goToDashboard,
    api: Routing.Actions.goToDashboard,
    [VISITOR_INSIGHTS]: Routing.Actions.goToVisitorInsights,
    [SPATIAL_INSIGHTS]: Routing.Actions.goToSpatialInsights,
    [COMMAND_LINE_TOOLS]: Routing.Actions.goToCommandLineTools,

    layers: LocusLabsMap.actions.showLayerErrors,
    wayfinding: LocusLabsMap.actions.wayfinding,
    support: VmsState.MultiThunks.showSupportModal,
    [IMPORT_EXPORT]: Routing.Actions.goToImportExport,
};

const SCENES = {
    [Routing.ROUTE_TYPES.DASHBOARD]: Venues,
    [Routing.ROUTE_TYPES.VENUES]: Venues,
    [Routing.ROUTE_TYPES.VENUE]: Venue,
    [Routing.ROUTE_TYPES.POI]: Venue,
    [Routing.ROUTE_TYPES.MAPNOTE]: Venue,
    [Routing.ROUTE_TYPES.ERROR_PAGE]: ErrorPage,
    [Routing.ROUTE_TYPES.MOD_WIZARD]: MODWizard,
    [Routing.ROUTE_TYPES.VISITOR_INSIGHTS]: VisitorInsights,
    [Routing.ROUTE_TYPES.SPATIAL_INSIGHTS]: SpatialInsights,
    [Routing.ROUTE_TYPES.COMMAND_LINE_TOOLS]: CommandLineToolsView,
    [Routing.ROUTE_TYPES.IMPORT_EXPORT]: ImportExportView,
};

class Dashboard extends Component {
    static propTypes = {
        banner: PropTypes.shape({ message: PropTypes.string, color: PropTypes.string, icon: PropTypes.string }),
        dispatch: PropTypes.func,
        email: PropTypes.string,
        env: PropTypes.string,
        firstName: PropTypes.string,
        hasPeopleDashboardsPermission: PropTypes.bool,
        hasSpatialInsightsPermission: PropTypes.bool,
        hasSpatialInsightsFeature: PropTypes.bool,
        hasMapsOnDisplayWritePermission: PropTypes.bool,
        internal: PropTypes.bool,
        isSubmenuOpen: PropTypes.bool.isRequired,
        lastName: PropTypes.string,
        modalIsOpen: PropTypes.bool.isRequired,
        modalOptions: PropTypes.object.isRequired,
        modalTextValues: PropTypes.object.isRequired,
        Scene: PropTypes.object,
        selectedMenu: PropTypes.string.isRequired,
        selectedSubmenu: PropTypes.string,
        venueId: PropTypes.string.isRequired,
        hasVenuesReadPermission: PropTypes.bool,
    };

    static defaultProps = {
        banner: { message: '', color: '', icon: '' },
        dispatch: () => {},
        email: '',
        env: 'prod',
        firstName: '',
        hasPeopleDashboardsPermission: false,
        hasSpatialInsightsPermission: false,
        hasSpatialInsightsFeature: false,
        hasMapsOnDisplayWritePermission: false,
        internal: false,
        lastName: '',
        Scene: null,
        selectedSubmenu: '',
        hasVenuesReadPermission: false,
    };

    handleChange = changes => {
        const { dispatch } = this.props;
        dispatch(VmsState.ModalReducer.Actions.setModalTextValues(changes));
    };

    onMenuSelect = menuItem => {
        const { dispatch, selectedMenu } = this.props;
        if (selectedMenu === MOD_WIZARD) {
            dispatch(VmsState.ViewReducer.Actions.askToConfirmMenuChange(menuItem));
        } else {
            const fn = menuActionCreators[menuItem];
            dispatch(VmsState.ViewReducer.Actions.setSelectedMenu(menuItem));
            if (fn) dispatch(fn());
        }
    };

    onSubmenuClick = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.ViewReducer.Actions.toggleSubmenu());
    };

    onSubmenuSelect = async submenuItem => {
        const { dispatch, venueId } = this.props;

        if (submenuItem === 'logout') await dispatch(VmsState.MultiThunks.logout(venueId));

        if (submenuItem === 'profile') dispatch(VmsState.MultiThunks.showProfileModal());

        if (submenuItem === 'password') dispatch(VmsState.MultiThunks.showPasswordModal());

        dispatch(VmsState.ViewReducer.Actions.setSelectedSubmenu(submenuItem));
    };

    componentDidUpdate(nextProps) {
        const { menuItemToBeChanged } = this.props;
        if (nextProps.menuItemToBeChanged !== menuItemToBeChanged) {
            const { dispatch } = this.props;
            const fn = menuActionCreators[menuItemToBeChanged];
            dispatch(VmsState.ViewReducer.Actions.setSelectedMenu(menuItemToBeChanged));
            if (fn) dispatch(fn());
        }
    }

    render() {
        const {
            Scene,
            firstName,
            lastName,
            email,
            env,
            selectedSubmenu,
            internal,
            hasMapsOnDisplayWritePermission,
            banner,
            modalOptions,
            modalIsOpen,
            modalTextValues,
            isSubmenuOpen,
            hasVenuesReadPermission,
            hasPeopleDashboardsPermission,
            hasSpatialInsightsPermission,
            hasSpatialInsightsFeature,
        } = this.props;

        return (
            <div className={s.dashboard}>
                {/* main left-hand menu */}
                <Menu
                    selectedIcon={this.props.selectedMenu}
                    selectedSubmenu={selectedSubmenu}
                    onChange={this.onMenuSelect}
                    onSubmenuChange={this.onSubmenuSelect}
                    isSubmenuOpen={isSubmenuOpen}
                    onSubmenuClick={this.onSubmenuClick}
                    firstName={firstName}
                    lastName={lastName}
                    email={email}
                    env={env}
                    internal={internal}
                    hasPeopleDashboardsPermission={hasPeopleDashboardsPermission}
                    hasSpatialInsightsPermission={hasSpatialInsightsPermission}
                    hasSpatialInsightsFeature={hasSpatialInsightsFeature}
                    hasMapsOnDisplayWritePermission={hasMapsOnDisplayWritePermission}
                    hasVenuesReadPermission={hasVenuesReadPermission}
                />

                {modalIsOpen && (
                    <ModalWrapper
                        banner={banner}
                        editableValues={modalTextValues}
                        handleChange={this.handleChange}
                        options={modalOptions}
                    />
                )}

                {Scene && <Scene />}
            </div>
        );
    }
}

// :: String -> Component|undefined
const getScene = state => prop(Routing.Selectors.getLocationType(state), SCENES);

const getSelectedMenu = state => {
    const selectedMenu = VmsState.ViewReducer.Selectors.getSelectedSubmenu(state);
    if (selectedMenu) return selectedMenu;
    const locationType = Routing.Selectors.getLocationType(state);
    if (locationType === 'VENUES_ROUTE') return 'venues';
    if (locationType === 'IMPORT_EXPORT_ROUTE') return 'Import/Export';
    if (locationType === Routing.ROUTE_TYPES.MOD_WIZARD) return MOD_WIZARD;
    if (locationType === Routing.ROUTE_TYPES.VISITOR_INSIGHTS) return VISITOR_INSIGHTS;
    if (locationType === Routing.ROUTE_TYPES.SPATIAL_INSIGHTS) return SPATIAL_INSIGHTS;
    return 'venues';
};
/*
  Parent view with left hand menu for these sub-views:

  /venues           -  shows list of venues
  /venues/:id       - landing screen for a particular venue - has top bar, map or list view
  /pois             - List of POIs across venues
  /accounts         - Account management features
  /api              - ???
  /command-line-tools - run command line tools (develop only)

 */

const mapStateToProps = state => {
    const venueId = VmsState.Routing.Selectors.getLocationVenueId(state) || '';
    const user = VmsState.SessionReducer.Selectors.getUserData(state) || { given_name: '', family_name: '', email: '' };
    const modalOptions = VmsState.ModalReducer.Selectors.getModalOptions(state);
    const modalIsOpen = !isEmpty(modalOptions);
    const modalTextValues = VmsState.ModalReducer.Selectors.getModalTextValues(state);
    const menuItemToBeChanged = VmsState.ViewReducer.Selectors.getMenuItemToBeChanged(state) || null;

    const hasMapsOnDisplayWritePermission = VmsState.SessionReducer.Selectors.hasWritePermissionToResource(
        '*',
        LliamUtilities.CONSTANTS.MAPS_ON_DISPLAY,
        state,
    );
    const hasVenuesReadPermission = VmsState.SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.OBJECTSTORES,
        state,
    );
    const hasPeopleDashboardsPermission = VmsState.SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.PEOPLE_INSIGHT_DASHBOARDS,
        state,
    );
    const hasSpatialInsightsPermission = VmsState.SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.SPATIAL_INSIGHT_DASHBOARDS,
        state,
    );
    // The feature itself ships behind the flag: no tab until ?spatialInsights=true,
    // so a generic user never lands on a dashboard that is still not ready.
    const hasSpatialInsightsFeature = !!state?.featureFlags?.spatialInsights;

    return {
        Scene: getScene(state),
        firstName: user.given_name,
        lastName: user.family_name,
        email: user.email,
        internal: user['custom:Internal'] === 'true' ? true : false,
        banner: VmsState.SessionReducer.Selectors.getMessageBanner(state),
        isSubmenuOpen: VmsState.ViewReducer.Selectors.getIsSubmenuOpen(state),
        selectedSubmenu: VmsState.ViewReducer.Selectors.getSelectedSubmenu(state),
        selectedMenu: getSelectedMenu(state),
        env: VmsState.selectors.getEnvironment(),
        modalOptions,
        modalIsOpen,
        modalTextValues,
        venueId,
        menuItemToBeChanged,
        hasMapsOnDisplayWritePermission,
        hasVenuesReadPermission,
        hasPeopleDashboardsPermission,
        hasSpatialInsightsPermission,
        hasSpatialInsightsFeature,
    };
};

export default connect(mapStateToProps)(Dashboard);
